import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Box, Group, Button, MantineProvider, Skeleton } from "@mantine/core";
import { useListState } from "@mantine/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import Swal from "sweetalert2";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';

import SurveyQuestion from "./SurveyQuestion";
import CreateSurveyMain from "./CreateSurveyMain";

import SurveyModel from "../../../../models/Survey";
import QuestionModel from "../../../../models/Question";
import SpaceMemberFilterModel from "../../../../models/SpaceMemberFilters";

import { sutraTheme } from "../../../../collections/sutraTheme";
import { setTiptapSidemenu } from "../../../../store/app";

export default function CreateSurvey(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [surveyId, setSurveyId] = useState(null);
  const [questions, handlers] = useListState([]);
  const [currency, setCurrency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [filters, setFilters] = useState([]);

  const tiptapSidemenu = useSelector((state) => state.tiptapSidemenu);

  const form = useRef({});
  const timeoutId = useRef();

  const usedFilterIds = Array.isArray(questions)
  ? questions.reduce((acc, q) => {
      if (q.pod_member_filter_id !== null && q.pod_member_filter_id !== undefined) {
        acc.push(q.pod_member_filter_id);
      }
      return acc;
    }, [])
  : [];

  useEffect(() => {
    if (props.survey && props.survey.id) {
      setLoading(true);
      setSurveyId(props.survey.id);
      onFetch();
    } else {
      if (!props.survey) {
        setSurveyId(null);
        handlers.setState([]);
        setCurrency(null);
        setCount(1)
      }
    }
  }, [props]);

  useEffect(() => {
    if (!props.spaceId || !surveyId || questions.length == 0) {
      return;
    }

    onReorder(questions);

  }, [questions]);

  useEffect(() => {
    onFetchFilters();
  }, [])

  useEffect(() => {
    if (tiptapSidemenu.opened == "filter-members-close") {
      dispatch(setTiptapSidemenu({ opened: null }));
      onFetchFilters();
    }
  }, [tiptapSidemenu]);

  const onReorder = (data) => {
    const order = data.map((question, index) => {
      return { id: question.id || "new", position: index };
    }).filter((question) => question.id !== "new");
    QuestionModel.onReorder(
      props.spaceId,
      surveyId,
      order,
      (data) => {},
      (data) => {}
    );
  };

  const onFetch = () => {
    SurveyModel.onFetchById(
      props.survey.id,
      props.spaceId,
      (data) => {
        handlers.setState(data.questions);
        setCurrency(data.currency);

        setTimeout(() => {
          setLoading(false);
          setCount(1)
        }, 1000);
      },
      (data) => {}
    );
  };

  const onFetchFilters = () => {
    if (!window.$fetchedTopSlug) return;
    SpaceMemberFilterModel.onFetch(
      window.$fetchedTopSlug,
      (data) => {
        if (Array.isArray(data)) {
          setFilters(data);
        }
      },
      (error) => {}
    );
  };

  const onCreate = (close=true) => {
    SurveyModel.onCreate(
      props.spaceId,
      form.current,
      (data) => {
        setSurveyId(data.id);
        
        setTimeout(() => {
          onPushNewQuestion();
        }, 100);
      },
      (data) => {
        Swal.fire(data.title, data.message, "error");
      }
    );
  };

  const onUpdate = (close=true) => {
    if (!surveyId) {
      return;
    }

    SurveyModel.onUpdate(
      surveyId,
      props.spaceId,
      form.current,
      (data) => {
        if (props.onAdd && close){
          props.onAdd(surveyId)
        }
      },
      (data) => {
        Swal.fire(data.title, data.message, "error");
      }
    );
  };

  const onUpdateQuestion = (question, index) => {
    const newQuestion = { ...questions[index] };
    if (!newQuestion.id || newQuestion.id == question.id) {
      newQuestion.id = question.id;
      newQuestion.content = question.content;
      newQuestion.question_type = question.question_type;

      newQuestion.answers = question.answers;
      handlers.setItem(index, newQuestion);
    }
  };

  const onUpdateLastQuestion = (id) => {
    const question = questions[questions.length - 1];
    question.new = false;
    question.id = id;
    handlers.setState([...questions]);
  };

  const onDestroyQuestion = (question) => {
    const newQuestions = [...questions];
    for (let i = 0; i < newQuestions.length; i++) {
      const _question = newQuestions[i];
      if (_question.id == question.id) {
        newQuestions.splice(i, 1);
        break;
      }
    }

    handlers.setState(newQuestions);
  };

  const onAddQuestion = () => {
    if (!surveyId) {
      onCreate();
    } else if (surveyId) {
      onPushNewQuestion();
    }
  };

  const onPushNewQuestion = () => {
    const newQuestions = [...questions];
    newQuestions.push({ new: true, position: questions.length + 1, content: `Question ${questions.length + 1}` });
    handlers.setState(newQuestions);
  };

  const onSave = (close=true) => {
    if (loading) {
      return;
    }
    if (!surveyId) {
      onCreate(close);
    } else if (surveyId) {
      onUpdate(close);
    }
    if (close){
      setTimeout(() => {
        props.setOpened(false);
      }, 500);
    }
  };

  const onUpdateForm = (_form) => {
    form.current = { ..._form };
    if (surveyId){
      if (timeoutId.current){
        clearTimeout(timeoutId.current)
      }
      timeoutId.current = setTimeout(() => {
        onSave(false)
      }, 1000)
    }
  };

  const onUpdateFormFull = () => {
    setCount(count + 1)
  };

  const reorderQuestions = (array, from, to) => {
    const newArray = array.slice();
  
    if (
      from < 0 ||
      from >= newArray.length ||
      to < 0 ||
      to >= newArray.length
    ) {
      return newArray;
    }
  
    const item = newArray.splice(from, 1)[0];
    newArray.splice(to, 0, item);
  
    return newArray.map((item, index) => ({
      ...item,
      position: index
    }));
  };

  if (loading) {
    return (
      <Box>
        <Skeleton height={20} width="100%" mt={20} mb={20} />
        <Skeleton height={20} width="100%" mt={20} mb={20} />
      </Box>
    );
  }

  return (
    <>
      <CreateSurveyMain
        survey={props.survey}
        surveyId={surveyId}
        spaceId={props.spaceId}
        onUpdate={props.onUpdate}
        acceptPayments={props.acceptPayments}
        onUpdateForm={onUpdateForm}
        onUpdateFormFull={onUpdateFormFull}
        count={count}
        isFlexibleOptions={props.isFlexibleOptions}
      />
      <DndContext
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={({ active, over }) => {
          if (active.id !== over?.id) {
            const oldIndex = questions.findIndex((i) => i.id === active.id);
            const newIndex = questions.findIndex((i) => i.id === over?.id);
            const updatedItems = reorderQuestions(questions, oldIndex, newIndex);
            handlers.setState(updatedItems);
          }
        }}
        collisionDetection={closestCenter}
      >
        <SortableContext items={questions.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          {questions.map((question, index) => {
            return (
              <SurveyQuestion
                key={question.id}
                index={index}
                spaceId={props.spaceId}
                surveyId={surveyId}
                question={question}
                currency={currency ? currency : {}}
                onUpdate={onUpdateQuestion}
                onUpdateLast={onUpdateLastQuestion}
                onDestroy={onDestroyQuestion}
                acceptPayments={props.acceptPayments}
                quizMode={form.current ? form.current.quiz_mode : false}
                showKeys={form.current ? form.current.after_submit_show_score_and_keys : false}
                filters={filters}
                usedFilterIds={usedFilterIds}
                onUpdateQuestions={onFetch}
              />
            );
          })}
        </SortableContext>
      </DndContext>
      <Group position="apart">
        {!props.acceptPayments && (
          <Button
            variant="filled"
            radius="xl"
            mt={15}
            leftIcon={<FontAwesomeIcon icon={solid("plus")} />}
            sx={(theme) => ({
              backgroundColor: sutraTheme.colors.sutrablue[3],
              "&:hover": {
                backgroundColor: sutraTheme.colors.sutrablue[4]
              }
            })}
            onClick={() => onAddQuestion()}
          >
            {t("spaces.settings.survey.createSurveyForm.form.addQuestionButton")}
          </Button>
        )}
        <Group>
          <MantineProvider theme={{ primaryShade: 6 }}>
            <Button
              variant="filled"
              radius="xl"
              mt={15}
              color="green"
              leftIcon={<FontAwesomeIcon icon={solid("check")} />}
              onClick={() => onSave()}
            >
              {t("spaces.settings.survey.createSurveyForm.form.saveButton")}
            </Button>
          </MantineProvider>
          {props.extraButton && props.extraButton}
        </Group>
      </Group>
      <Box sx={{ height: 100 }} />
    </>
  );
}
